import React, { useEffect, useState, useCallback, useRef } from "react";
import { Modal } from "@vds/modals";
import { Icon } from "@vds/icons";
import "./UserGroupsModals.css";
import { Notification } from "@vds/notifications";
import { useDispatch, useSelector } from "react-redux";
import { setCheckedSites, setSelectedMembers } from "../../store/actions";
import { fetchNotificationUser } from "./userGroupsService";
import {
  fetchSiteList,
  renderStepOneContent,
  renderStepThreeContent,
  renderStepTwoContent,
  renderStepFourContent,
} from "./Utils";
import { addUserGroupsSubmit } from "./userGroupsService";
import { useNavigate } from "react-router-dom";
import { showNotificationBanner } from "../../store/actions";
import { getUsersFilteredBySiteAndRole } from "../../model/roleService";
import { USER_GROUP_DETAILS_CONSTANTS } from "../../global/constants/constants";

const AddGroupModal = ({
  title,
  showModal = false,
  setShowModal,
  stepOneTitle,
  stepTwoTitle,
  stepThreeTitle,
  stepFourTitle,
  stepTwoBody,
  stepThreeBody,
  onSuccess,
  setShowNotification,
  showNotification,
  bannerType,
  setBannerType,
  notificationDataSource,
}) => {
  const { activeSession, groupMembers } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [step, setStep] = useState(1);
  const [groupName, setGroupName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [siteList, setSiteList] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [allSitesChecked, setAllSitesChecked] = useState(false);
  const [userData, setUserData] = useState([]);
  const [filteredUserData, setFilteredUserData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState([]);
  const [selectedSiteName, setSelectedSiteName] = useState("");
  const [showCreateGroup, setShowCreateGroup] = useState({
    isOpen: false,
    isLoading: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });
  const [hasFetchedUsers, setHasFetchedUsers] = useState(false);
  const dispatch = useDispatch();
  const { checkedSites, selectedMembers } = useSelector(
    (state) => state.addGroupReducer
  );
  const localCachedUsersRef = useRef([]);
  const localCachedSiteIdRef = useRef(null);
  const selectedSiteRef = useRef();
  const lastFetchedSiteIdRef = useRef(null);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const navigate = useNavigate();

  useEffect(() => {
    if (showModal) {
      dispatch(setSelectedMembers([]));
      setStep(1);
      setHasFetchedUsers(false);
    }
  }, [showModal, dispatch]);

  useEffect(() => {
    setSiteList(siteTagList?.sites);
  }, []);

  useEffect(() => {
    const hideNotification = () => {
      setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
    };
    if (notification.show) {
      hideNotification();
    }
  }, [notification]);

  useEffect(() => {
    if (!hasFetchedUsers) {
      setLoading(true);
    }
  }, [hasFetchedUsers]);

  const fetchUsers = async () => {
    try {
      const apiResponse = await getUsersFilteredBySiteAndRole(
      activeSession,
      [],
      [selectedSiteRef?.current?.id] || []
    );
      const users = apiResponse || [];
      localCachedUsersRef.current = users;
      localCachedSiteIdRef.current = selectedSiteRef?.current?.id;
      setUserData(users);
      setFilteredUserData(users);
      setHasFetchedUsers(true);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const searchedValue = e?.target?.value;
    setSearchQuery(searchedValue);
    if (searchedValue?.trim() === "") {
      setFilteredUserData(userData);
    } else {
      const filtered = userData?.filter((user) =>
        `${user?.firstname} ${user?.lastname}`
          .toLowerCase()
          .includes(searchedValue?.toLowerCase())
      );
      setFilteredUserData(filtered);
    }
  };

  const filteredMembers = groupMembers?.filter((member) =>
    `${member.firstname} ${member.lastname} ${member.email}`.toLowerCase().includes(searchQuery)
  );

  const getPayload = () => {
    return {
      accountname: activeSession.accountId,
      group: {
        name: groupName,
        tagids: checkedSites,
      },
      userids: selectedMembers?.map((member) => member.id),
    };
  };

  const handleCloseModal = () => {
    setUserData([]);
    setFilteredUserData([]);
    selectedSiteRef.current = null;
    lastFetchedSiteIdRef.current = null;
    setShowModal(false);
    setGroupName("");
    setSelectedSites([]);
    setAllSitesChecked([]);
    setSelectedUser([]);
    dispatch(setCheckedSites([]));
    setSelectedSiteName("");
  };

  const setError = (message) => {
    setErrorMessage(message);
  };

  const handleCheckboxChange = (user) => {
    const isSelected = selectedMembers?.some(
      (selectedUser) => selectedUser.id === user.id
    );

    const updatedSelectedMembers = isSelected
      ? selectedMembers.filter((selectedUser) => selectedUser.id !== user.id)
      : [...selectedMembers, user];

    dispatch(setSelectedMembers(updatedSelectedMembers));
  };

  const handleAllSitesToggle = () => {
    const updatedList = siteList?.map((site) => ({
      ...site,
      checked: !site.checked,
    }));
    setSiteList(updatedList);
    setSelectedSites(
      updatedList?.filter((site) => site.checked).map((site) => site.title)
    );
    setIsValid(updatedList?.some((site) => site.checked));
  };

  const handleSiteToggle = (selectedSiteId, filteredSites) => {
    const selectedSite = filteredSites.find(
      (site) => site.id === selectedSiteId
    );

    if (selectedSite) {
      selectedSiteRef.current = selectedSite
      setSelectedSites(prevSites => [...prevSites,selectedSite])
      setIsValid(true);
      setSelectedSiteName(selectedSiteId);
    } else {
      selectedSiteRef.current = null
      setSelectedSites([]);
      setIsValid(false);
    }
  };

  const handleGroupNameChange = (e) => {
    const groupName = e.target.value;
    let errorMessage = "";
    setGroupName(groupName);
    const groupExists = notificationDataSource?.some(
      (group) => group?.Groups === groupName?.trim()
    );
    if (groupExists) {
      errorMessage = USER_GROUP_DETAILS_CONSTANTS.GROUP_NAME_DUPLICATE_ERROR;
      setIsValid(false);
      setErrorMessage(errorMessage);
    } else if (groupName.trim() === "") {
      errorMessage = USER_GROUP_DETAILS_CONSTANTS.GROUP_NAME_EMPTY_ERROR;
      setIsValid(false);
      setErrorMessage(errorMessage);
    } else {
      setIsValid(true);
      setErrorMessage("");
    }
  };

  const handleNext = async () => {
    setLoading(true);
    let isValid = false;
    let errorMessage = "";

    try {
      if (step === 1) {
        const checkedSitesData = siteList
          .filter((site) =>
            selectedSites.some((selectedSite) => selectedSite.id === site.id)
          )
          .map((site) => site.id);
        dispatch(setCheckedSites(checkedSitesData));
        isValid = checkedSitesData.length > 0;
        setIsValid(isValid);
        if (isValid) setStep(2);
      } else if (step === 2) {
        isValid = errorMessage?.length === 0;
        setIsValid(isValid);
        if (isValid) {
          setStep(3);
        }
        if (
          selectedSiteRef?.current?.id &&
          selectedSiteRef.current.id !== lastFetchedSiteIdRef.current
        ) {
          const cachedUsers = localCachedUsersRef.current;
          const isSameSite = localCachedSiteIdRef.current === selectedSiteRef.current.id;
          if (cachedUsers.length > 0 && isSameSite) {
            setUserData(cachedUsers);
            setFilteredUserData(cachedUsers);
            setLoading(false);
            fetchUsers();
          } else {
            await fetchUsers();
          }
          lastFetchedSiteIdRef.current = selectedSiteRef.current.id;
        }
      } else if (step === 3) {
        const payload = getPayload(groupName, checkedSites, selectedMembers);
        setStep(4);
      } else if (step === 4) {
        const payload = getPayload(groupName, checkedSites, selectedMembers);
        const result = await addUserGroupsSubmit(payload, activeSession);
        if (result?.success) {
          console.log(result.status, "res status");
          const notificationInfo = {
            type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_ADDED,
            priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_SUCCESS,
            data: {
              subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_ADDED_SUCCESS(groupName),
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
          navigate("/usergroups", {
            state: { response: result },
          });
          onSuccess();
        } else {
          console.error("Failed to remove user, status:", result.status);
          const notificationInfo = {
            type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_ADD_FAILED,
            priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
            data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_ADDED_FAILURE(groupName) },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
        }

        handleCloseModal();
      } else {
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error while loading:", error);
    } finally {
      setLoading(false);
      setError(errorMessage);
    }
  };

  const handleBack = () => {
    switch (step) {
      case 4:
        setStep(3);
        break;
      case 3:
        setStep(2);
        break;
      case 2:
        setStep(1);
        break;
      case 1:
        handleCloseModal();
        break;
      default:
    }
  };

  const siteWrapper = (siteList) => {
    let list = [];
    siteList?.sites?.forEach((tag) => {
      list.push({
        id: tag.id,
        title: tag.name,
        checked: false,
      });
    });
    return list;
  };

  const renderStepContent = (step, titles) => {
    const {
      stepOneTitle,
      stepTwoTitle,
      stepTwoBody,
      stepThreeTitle,
      stepThreeBody,
      stepFourTitle,
    } = titles;
    let filteredSites = siteList || [];

    if (searchValue.trim() !== "") {
      filteredSites = siteList.filter(
        (site) =>
          site?.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
          site?.subtitle?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    switch (step) {
      case 1:
        return renderStepOneContent({
          // searchValue,
          filteredSites,
          handleSiteToggle,
          selectedSiteName,
          allSitesChecked,
          isValid,
          handleBack,
          handleNext,
          title: stepTwoTitle,
          body: stepTwoBody,
        });
      case 2:
        return renderStepTwoContent({
          groupName,
          setGroupName,
          errorMessage,
          handleBack,
          handleNext,
          handleGroupNameChange,
          isValid,
          title: stepOneTitle,
        });

      case 3:
        return renderStepThreeContent({
          userData: filteredUserData,
          selectedMembers,
          handleCheckboxChange,
          handleBack,
          handleNext,
          title: stepThreeTitle,
          searchQuery,
          handleSearchChange,
          selectedMembers: filteredMembers,
          body: stepThreeBody,
          newSelectedMembers: selectedUser,
          setNewSelectedMembers: setSelectedUser,
          page: USER_GROUP_DETAILS_CONSTANTS.PAGE_ADD_GROUP_MODAL,
          loading,
        });

      case 4:
        return renderStepFourContent({
          handleBack,
          handleNext,
          title: stepFourTitle,
          groupName,
          checkedSites,
          selectedMembers,
          siteTagList,
        });

      default:
        return null;
    }
  };

  return (
    <div>
      <Modal
        width={step === 3 ? "60vw" : ""}
        opened={showModal}
        disableAnimation={false}
        disableOutsideClick={true}
        className="user-groups-modal"
        hideCloseButton={true}
        closeButton={
          <div
            className="modal-close-button"
            data-testid="addGroupModal_closeButton"
            onClick={handleCloseModal}
          >
            <Icon name="close" ariaHidden={true} />
          </div>
        }
        onOpenedChange={(opened) => {
          if (!opened) {
            handleCloseModal();
          }
        }}
        ariaLabel={title}
      >
        {renderStepContent(step, {
          stepOneTitle,
          stepTwoTitle,
          stepTwoBody,
          stepThreeTitle,
          stepThreeBody,
          stepFourTitle,
        })}
      </Modal>
    </div>
  );
};

export default AddGroupModal;
